import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
    baseURL: `${API_URL}/api`,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Handle auth errors and normalize error messages
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            const isDeployLogout = error.response?.headers?.['x-deploy-logout'] === 'true' ||
                                   error.response?.data?.detail?.includes('Update');
            
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            
            if (isDeployLogout) {
                // Store message to show on login page
                sessionStorage.setItem('deploy_logout', 'true');
            }
            
            window.location.href = '/login';
        }
        
        // Normalize error detail to string
        if (error.response?.data?.detail) {
            const detail = error.response.data.detail;
            if (typeof detail !== 'string') {
                if (Array.isArray(detail)) {
                    error.response.data.detail = detail.map(e => e.msg || e.message || String(e)).join(', ');
                } else if (typeof detail === 'object') {
                    error.response.data.detail = detail.msg || detail.message || JSON.stringify(detail);
                }
            }
        }
        
        return Promise.reject(error);
    }
);

// Auth
export const login = (email, password) => api.post('/auth/login', { email, password });
export const register = (data) => api.post('/auth/register', data);
export const getMe = () => api.get('/auth/me');
export const changePassword = (newPassword) => api.put('/auth/change-password', { new_password: newPassword });
export const seedData = () => api.post('/seed');

// Users
export const getUsers = () => api.get('/users');
export const getEmployees = () => api.get('/users/employees');
export const getTerminalEmployees = () => api.get('/users/terminal-employees');
export const getAssignableUsers = () => api.get('/users/assignable');
export const getAllEmployees = () => api.get('/users/all-employees');
export const getAllEmployeesWithTerminated = () => api.get('/users/all-employees-with-terminated');
export const debugTerminalEmployees = () => api.get('/debug/terminal-employees');
export const deleteUser = (id) => api.delete(`/users/${id}`);
export const updateEmployeeWeeklyHours = (id, weeklyHours, workType = null, workDaysPerWeek = null, showOnTerminal = null, hasSmartphone = null, employmentStartDate = null, terminationDate = null, workStartTime = null, workEndTime = null, workDays = null) => api.put(`/employees/${id}/weekly-hours`, { 
    weekly_hours: weeklyHours,
    work_type: workType,
    work_days_per_week: workDaysPerWeek,
    show_on_terminal: showOnTerminal,
    has_smartphone: hasSmartphone,
    employment_start_date: employmentStartDate,
    termination_date: terminationDate,
    work_start_time: workStartTime,
    work_end_time: workEndTime,
    work_days: workDays
});
export const getEmployeeHoursSummary = (id) => api.get(`/employees/${id}/hours-summary`);
export const createEmployeeDirect = (data) => api.post('/employees/create-direct', data);
export const resetEmployeePassword = (id, newPassword = null) => api.put(`/employees/${id}/reset-password`, newPassword ? { new_password: newPassword } : {});

// Admin Management
export const getAdmins = () => api.get('/admins');
export const createAdmin = (data) => api.post('/admins', data);
export const updateAdmin = (id, data) => api.put(`/admins/${id}`, data);
export const deleteAdmin = (id) => api.delete(`/admins/${id}`);

// Invitations
export const getInvitations = () => api.get('/invitations');
export const createInvitation = (data) => api.post('/invitations', data);
export const deleteInvitation = (id) => api.delete(`/invitations/${id}`);
export const validateInvitation = (token) => api.get(`/invitations/validate/${token}`);
export const acceptInvitation = (data) => api.post('/invitations/accept', data);

// Customers
export const getCustomers = () => api.get('/customers');
export const getCustomer = (id) => api.get(`/customers/${id}`);
export const createCustomer = (data) => api.post('/customers', data);
export const updateCustomer = (id, data) => api.put(`/customers/${id}`, data);
export const deleteCustomer = (id) => api.delete(`/customers/${id}`);

// Orders
export const getOrders = (params) => api.get('/orders', { params });
export const getArchivedOrders = () => api.get('/orders', { params: { archived: true } });
export const getOrder = (id) => api.get(`/orders/${id}`);
export const createOrder = (data) => api.post('/orders', data);
export const updateOrder = (id, data) => api.put(`/orders/${id}`, data);
export const updateOrderWorkItems = (id, items) => api.put(`/orders/${id}/work-items`, items);
export const assignOrder = (id, userId) => api.post(`/orders/${id}/assign?user_id=${userId}`);
export const startOrder = (id) => api.post(`/orders/${id}/start`);
export const completeOrder = (id, data) => api.post(`/orders/${id}/complete`, data);
export const downloadOrderPdf = (id) => api.get(`/orders/${id}/pdf`, { responseType: 'blob' });
export const downloadOrdersOverviewPdf = (filterType, customerId, employeeId, dateFrom, dateTo) => {
    const params = { filter_type: filterType };
    if (customerId) params.customer_id = customerId;
    if (employeeId) params.employee_id = employeeId;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    return api.get('/orders/export/pdf', { params, responseType: 'blob' });
};
export const archiveOrder = (id) => api.post(`/orders/${id}/archive`);
export const deleteOrder = (id) => api.delete(`/orders/${id}`);

// System Settings
export const getSystemSettings = () => api.get('/settings');
export const updateSystemSettings = (data) => api.put('/settings', data);

// AI Scheduling
export const aiScheduleOrder = (data) => api.post('/ai/schedule', data);

// Export
export const exportOrderCSV = (id) => api.get(`/orders/${id}/export/csv`, { responseType: 'blob' });
export const exportOrderJSON = (id) => api.get(`/orders/${id}/export/json`);

// Stats
export const getStats = () => api.get('/stats');
export const getAdminDashboardOverview = () => api.get('/admin/dashboard-overview');

// Time Tracking
export const getTimeEntries = (userId) => api.get('/time-entries', { params: { user_id: userId } });
export const createTimeEntry = (data) => api.post('/time-entries', data);
export const updateTimeEntry = (id, data) => api.put(`/time-entries/${id}`, data);
export const deleteTimeEntry = (id) => api.delete(`/time-entries/${id}`);
export const getTimeSummary = (month, year, userId) => api.get('/time-entries/summary', { params: { month, year, user_id: userId } });
export const exportTimePDF = (month, year, userId) => api.get('/time-entries/export-pdf', { params: { month, year, user_id: userId }, responseType: 'blob' });
export const getTimeBalance = (userId) => api.get('/time-entries/balance', { params: { user_id: userId } });

// Terminal Clock In/Out (authenticated - legacy)
export const clockIn = (userId, deviceDate, deviceTime) => api.post('/terminal/clock-in', { 
    user_id: userId,
    device_date: deviceDate,
    device_time: deviceTime
});
export const clockOut = (userId, deviceDate, deviceTime, tookBreak = null, earlyLeaveReason = null, breakMinutes = 30) => api.post('/terminal/clock-out', { 
    user_id: userId,
    device_date: deviceDate,
    device_time: deviceTime,
    took_break: tookBreak,
    break_minutes: breakMinutes,
    early_leave_reason: earlyLeaveReason
});

// Public Terminal API (no authentication required)
const publicApi = axios.create({
    baseURL: `${API_URL}/api/public/terminal`,
    headers: { 'Content-Type': 'application/json' }
});

export const publicTerminalGetEmployees = () => publicApi.get('/employees');
export const publicTerminalGetStatus = (userId) => publicApi.get(`/employee/${userId}/status`);
export const publicTerminalGetTimeEntries = (userId) => publicApi.get(`/employee/${userId}/time-entries`);
export const publicTerminalClockIn = (userId, deviceDate, deviceTime) => publicApi.post('/clock-in', { 
    user_id: userId,
    device_date: deviceDate,
    device_time: deviceTime
});
export const publicTerminalClockOut = (userId, deviceDate, deviceTime, tookBreak = null, earlyLeaveReason = null, breakMinutes = 30) => publicApi.post('/clock-out', { 
    user_id: userId,
    device_date: deviceDate,
    device_time: deviceTime,
    took_break: tookBreak,
    break_minutes: breakMinutes,
    early_leave_reason: earlyLeaveReason
});

// Admin Time Management
export const adminGetTimeEntries = (params) => api.get('/admin/time-entries', { params });
export const adminCreateTimeEntry = (data) => api.post('/admin/time-entries', data);
export const adminUpdateTimeEntry = (id, data) => api.put(`/admin/time-entries/${id}`, data);
export const adminDeleteTimeEntry = (id) => api.delete(`/admin/time-entries/${id}`);
export const getOpenTimeEntries = () => api.get('/admin/time-entries/open');
export const closeOpenTimeEntry = (entryId, endTime, breakMinutes = 30) => api.post('/admin/time-entries/close-open', { 
    entry_id: entryId,
    end_time: endTime,
    break_minutes: breakMinutes
});

// Vacations
export const getVacations = (params) => api.get('/vacations', { params });
export const createVacation = (data) => api.post('/vacations', data);
export const adminCreateVacation = (data) => api.post('/vacations/admin-create', data);
export const approveVacation = (id) => api.put(`/vacations/${id}/approve`);
export const rejectVacation = (id) => api.put(`/vacations/${id}/reject`);
export const deleteVacation = (id) => api.delete(`/vacations/${id}`);
export const getVacationCalendar = (params) => api.get('/vacations/calendar', { params });

// Calendar ICS Feed
export const getIcsToken = () => api.get('/calendar/ics-token');

// Time Change Requests
export const getTimeChangeRequests = (params) => api.get('/time-change-requests', { params });
export const createTimeChangeRequest = (data) => api.post('/time-change-requests', data);
export const approveTimeChangeRequest = (id) => api.put(`/time-change-requests/${id}/approve`);
export const rejectTimeChangeRequest = (id, reason) => api.put(`/time-change-requests/${id}/reject`, null, { params: { reason } });
export const deleteTimeChangeRequest = (id) => api.delete(`/time-change-requests/${id}`);
export const cleanupOldTimeChangeRequests = () => api.delete('/admin/time-change-requests/cleanup');

// Proxy Time Submission (submit time for others)
export const getProxyUsers = () => api.get('/time-change-requests/proxy-users');
export const createTimeRequestForOther = (targetUserId, data) => api.post(`/time-change-requests/for/${targetUserId}`, data);
export const setProxyPermissions = (userId, targetUserIds) => api.put(`/admin/users/${userId}/proxy-permissions`, targetUserIds);

// Push Notifications
export const getVapidPublicKey = () => api.get('/push/vapid-public-key');
export const subscribeToPush = (subscription) => api.post('/push/subscribe', subscription);
export const unsubscribeFromPush = () => api.delete('/push/unsubscribe');

// GPS Tracking
export const updateLocation = (data) => api.post('/tracking/location', data);
export const getTrackingLocations = () => api.get('/tracking/locations');
export const getLocationHistory = (userId, date) => api.get(`/tracking/history/${userId}`, { params: { date } });
export const getMyClockStatus = () => api.get('/tracking/my-status');
export const updateMyLocation = (latitude, longitude, accuracy) => 
    api.post('/tracking/my-location', null, { params: { latitude, longitude, accuracy } });

// Employee Setup
export const completeSetup = () => api.post('/setup/complete');

// Water Extraction (Bewässerungsentnahme)
export const getWaterExtractions = (startDate, endDate, customerId) => 
    api.get('/water-extractions', { params: { start_date: startDate, end_date: endDate, customer_id: customerId } });
export const createWaterExtraction = (data) => api.post('/water-extractions', data);
export const deleteWaterExtraction = (id) => api.delete(`/water-extractions/${id}`);
export const getWaterExtractionSummary = (startDate, endDate) => 
    api.get('/water-extractions/summary', { params: { start_date: startDate, end_date: endDate } });
export const exportWaterExtractionsPdf = (startDate, endDate, customerId) => 
    api.get('/water-extractions/pdf', { 
        params: { start_date: startDate, end_date: endDate, customer_id: customerId },
        responseType: 'blob'
    });

// Calendar Events
export const getCalendarEvents = (month, year) => 
    api.get('/calendar/events', { params: { month, year } });
export const createCalendarEvent = (data) => api.post('/calendar/events', data);
export const updateCalendarEvent = (id, data) => api.put(`/calendar/events/${id}`, data);
export const deleteCalendarEvent = (id) => api.delete(`/calendar/events/${id}`);

// Calendar Reminders
export const getDueReminders = () => api.get('/calendar/reminders');
export const dismissReminder = (eventId) => api.post(`/calendar/reminders/${eventId}/dismiss`);

// Irrigation Control
export const getIrrigationConfig = () => api.get('/irrigation/config');
export const getIrrigationCredentials = () => api.get('/irrigation/credentials');
export const saveIrrigationCredentials = (data) => api.post('/irrigation/credentials', data);
export const getIrrigationSchedules = () => api.get('/irrigation/schedules');
export const getIrrigationHistory = () => api.get('/irrigation/history');
export const createIrrigationSchedule = (data) => api.post('/irrigation/schedule', data);
export const deleteIrrigationSchedule = (id) => api.delete(`/irrigation/schedule/${id}`);
export const cancelIrrigationSchedule = (id) => api.post(`/irrigation/schedule/${id}/cancel`);
export const resetStuckIrrigation = (id) => api.post(`/irrigation/schedule/${id}/reset`);
export const stopAllIrrigation = () => api.post('/irrigation/stop-all');

// Projects
export const getProjects = (params) => api.get('/projects', { params });
export const getProject = (id) => api.get(`/projects/${id}`);
export const createProject = (data) => api.post('/projects', data);
export const updateProject = (id, data) => api.put(`/projects/${id}`, data);
export const deleteProject = (id) => api.delete(`/projects/${id}`);
export const assignOrderToProject = (orderId, projectId) => api.put(`/orders/${orderId}/assign-project`, null, { params: { project_id: projectId } });

// Payroll / Time Account
export const getMonthlyPayroll = (year, month) => api.get(`/payroll/month/${year}/${month}`);
export const getEmployeePayroll = (employeeId, year) => api.get(`/payroll/employees/${employeeId}`, { params: { year } });
export const submitPayroll = (employeeId, year, month, data) => api.post(`/payroll/employees/${employeeId}/submit/${year}/${month}`, data);

// Admin Tasks / Verwaltung
export const getAdminTasks = (params) => api.get('/admin-tasks', { params });
export const getAdminTask = (id) => api.get(`/admin-tasks/${id}`);
export const createAdminTask = (data) => api.post('/admin-tasks', data);
export const updateAdminTask = (id, data) => api.put(`/admin-tasks/${id}`, data);
export const deleteAdminTask = (id) => api.delete(`/admin-tasks/${id}`);
export const getTaskDashboardSummary = () => api.get('/admin-tasks/dashboard/summary');
export const uploadTaskAttachment = (taskId, formData) => api.post(`/admin-tasks/${taskId}/attachments`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
});
export const deleteTaskAttachment = (taskId, attachmentId) => api.delete(`/admin-tasks/${taskId}/attachments/${attachmentId}`);

export default api;
